import DocLayout from '../../components/docs/DocLayout'
import Flag from '../../components/marks/Flag'
import AgencyMark from '../../components/marks/AgencyMark'
import Icon from '../../components/docs/Icon'
import { OfficialNotice } from '../../components/site/Banner'
import { BANNER } from '@/content/identity'
import Identifier from '../../components/site/Identifier'
import Lockup from '../../components/site/Lockup'
import { agencies, america, findMission } from '@/content/missions'
import { Link } from 'react-router-dom'

/** A department, a sub-agency and an independent agency from each corner of government. */
const sampleAgencies = ['dod', 'usda', 'fema', 'nasa', 'nps', 'usps']
  .map((id) => agencies.find((agency) => agency.id === id))
  .filter((agency) => agency !== undefined)
import { usePrefs } from '@/state/prefs'
import { markStyleInfo as styleNotes, markStyles } from '@/content/marks'
import Cased from '@/components/ui/Cased'

const legal = [
  {
    cite: '18 U.S.C. 701',
    text: 'Restricts making or displaying the insignia of a federal department or agency, or a colorable imitation, except as its regulations allow.',
    url: 'https://www.law.cornell.edu/uscode/text/18/701',
  },
  {
    cite: '18 U.S.C. 713',
    text: 'Restricts using the Great Seal of the United States in a way that conveys a false impression of government sponsorship or approval.',
    url: 'https://www.law.cornell.edu/uscode/text/18/713',
  },
  {
    cite: '18 U.S.C. 1017',
    text: 'Makes it a crime to fraudulently or wrongfully affix or impress an agency seal.',
    url: 'https://www.law.cornell.edu/uscode/text/18/1017',
  },
  {
    cite: '31 U.S.C. 333',
    text: 'Protects the names, symbols and emblems of the Department of the Treasury and its bureaus, without regard to any disclaimer.',
    url: 'https://www.law.cornell.edu/uscode/text/31/333',
  },
  {
    cite: '42 U.S.C. 1320b-10',
    text: 'Protects the names, letters and emblems of the Social Security Administration, HHS and Medicare, without regard to any disclaimer.',
    url: 'https://www.law.cornell.edu/uscode/text/42/1320b-10',
  },
  {
    cite: '4 U.S.C. 8',
    text: 'The Flag Code: the flag should never be used for advertising purposes or printed on items meant to be discarded.',
    url: 'https://www.law.cornell.edu/uscode/text/4/8',
  },
]

export default function IdentityPage() {
  const { prefs, set } = usePrefs()
  const mission = findMission(prefs.mission) ?? america
  // The identifier is an agency pattern, so the default theme shows an example agency.
  const identified = mission.id === america.id ? findMission('dod')! : mission

  return (
    <DocLayout section={{ label: 'Foundations', to: '/foundations' }}
      eyebrow="Foundations"
      title="Identity"
      intro={
        <p>
          A site says who runs it before it says anything else: the banner, the mark and the identifier. MDS draws
          every mark in six styles, so the same identity works from a favicon to a hero.
        </p>
      }
      wide
      aside={
        <div className="fnd-identity-hero">
          <Lockup />
        </div>
      }
    >
      <section className="doc-section" aria-labelledby="identity-flag">
        <h2 id="identity-flag"><Cased>The flag</Cased></h2>
        <p>
          The flag marks a site for the whole nation. Official is drawn to the proportions in Executive Order 10834,
          1.9 to 1, in its own colors in every theme. The other styles take the theme's ink and flag roles.
        </p>
        <div className="fnd-marks" role="group" aria-label="Flag styles">
          {markStyles.map((style) => (
            <button
              key={style}
              type="button"
              className="fnd-marks__item"
              aria-pressed={prefs.mark === style}
              onClick={() => set('mark', style)}
            >
              <span className="fnd-marks__art fnd-marks__art--flag">
                <AgencyMark mission={america} variant={style} />
              </span>
              <span className="fnd-marks__name">{styleNotes[style].name}</span>
              <span className="fnd-marks__use">{styleNotes[style].use}</span>
            </button>
          ))}
        </div>
      </section>

      <section className="doc-section" aria-labelledby="identity-agency">
        <h2 id="identity-agency"><Cased>Agency marks</Cased></h2>
        <p>
          An agency has a flag, a seal and often a logo of its own, such as the Pentagon symbol. MDS shows each as
          published. The simple, pixel and line styles are original emblems drawn from a motif in the seal: the
          eagle, the globe, the wheat sheaf.
        </p>
        <div className="doc-table-wrap" tabIndex={0}>
          <table className="doc-table fnd-agency-table">
            <thead>
              <tr>
                <th scope="col">Agency</th>
                {markStyles.map((style) => (
                  <th key={style} scope="col">
                    {styleNotes[style].name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sampleAgencies.map((agency) => (
                <tr key={agency.id} data-mission={agency.id}>
                  <th scope="row">
                    <span className="fnd-agency-table__name">{agency.name}</span>
                    <span className="mds-muted">Motif: {agency.motif}</span>
                  </th>
                  {markStyles.map((style) => (
                    <td key={style}>
                      <AgencyMark
                        mission={agency}
                        variant={style}
                        className="fnd-agency-table__mark"
                        title={`${agency.name}, ${styleNotes[style].name.toLowerCase()} style`}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p>
          <Link className="mds-link" to="/missions">
            Every agency's marks
          </Link>{' '}
          are on its own mission page.
        </p>
        <p className="mds-muted">
          Seals are reproduced from Wikimedia Commons, which records each file's source. An agency that adopts MDS
          supplies its own seal file.
        </p>
      </section>

      <section className="doc-section" aria-labelledby="identity-banner">
        <h2 id="identity-banner"><Cased>Banner</Cased></h2>
        <p>
          A single centered line above the header, small and quiet, with an information button that opens a dialog. The
          wording depends on who runs the site.
        </p>
        <div className="fnd-banners">
          <figure className="fnd-banner">
            <div className="fnd-banner__bar">
              <Flag variant="official" className="fnd-banner__flag" />
              <span>{BANNER.official}</span>
              <Icon name="info" size={14} />
            </div>
            <figcaption>
              <strong>Official.</strong> For a site an agency runs on its .gov or .mil domain over HTTPS. This is the notice
              in the{' '}
              <a href="https://designsystem.digital.gov/components/banner/">USWDS banner guidance</a>.
            </figcaption>
          </figure>
          <figure className="fnd-banner">
            <div className="fnd-banner__bar">
              <Flag variant="simple" className="fnd-banner__flag" />
              <span>{BANNER.site}</span>
              <Icon name="info" size={14} />
            </div>
            <figcaption>
              <strong>Alternate.</strong> For a site about government that an agency does not run, such as this one. Its
              dialog says who runs the site.
            </figcaption>
          </figure>
        </div>
        <h3><Cased>The official dialog</Cased></h3>
        <div className="doc-specimen">
          <OfficialNotice />
        </div>
      </section>

      <section className="doc-section" aria-labelledby="identity-identifier">
        <h2 id="identity-identifier"><Cased>Identifier</Cased></h2>
        <p>
          The identifier closes an agency site: its domain, its parent agency, the links every agency site carries and a
          pointer to USA.gov. It appears for {identified.name} below; choose another mission to see it change.
        </p>
        <div className="fnd-identifier">
          <Identifier mission={identified} />
        </div>
      </section>

      <section className="doc-section" aria-labelledby="identity-lockup">
        <h2 id="identity-lockup"><Cased>Lockup</Cased></h2>
        <p>
          The mark sits to the left of the name, set in the display serif, with a gap of three-eighths of the mark's
          height. Keep clear space of one mark height on every side.
        </p>
        <div className="fnd-lockups">
          <div className="fnd-lockup" data-theme="light">
            <Lockup />
          </div>
          <div className="fnd-lockup" data-theme="dark">
            <Lockup />
          </div>
        </div>
      </section>

      <section className="doc-section" aria-labelledby="identity-law">
        <h2 id="identity-law"><Cased>Seals, names and the flag</Cased></h2>
        <p>
          Federal law governs who may display an agency's seal or name. A site an agency runs displays its own seal; any
          other use follows the statutes and the agency's regulations below.
        </p>
        <ul className="fnd-law">
          {legal.map((item) => (
            <li key={item.cite}>
              <a href={item.url} className="fnd-law__cite">
                {item.cite}
              </a>
              <span>{item.text}</span>
            </li>
          ))}
        </ul>
      </section>
    </DocLayout>
  )
}
